import { ConfigProvider } from 'antd'
import { useAppSetting } from '../appStore'
import { theme as antdTheme } from 'antd'
import { TLanguages } from 'core/config/locale'
import { useState, useEffect } from 'react'
import type { Locale } from 'antd/es/locale'

const langMap: Record<TLanguages, string> = {
  en_US: 'en_US', // English
  zh_CN: 'zh_CN', // Chinese (Simplified)
  zh_HK: 'zh_TW', // Chinese (Traditional) - Uses zh_TW as Antd doesn't have zh_HK
  th_TH: 'th_TH', // Thai
  fil_PH: 'en_US', // Filipino - Ant Design doesn't have Filipino, fallback to English
  es_ES: 'es_ES', // Spanish
  ja_JP: 'ja_JP', // Japanese
  pt_PT: 'pt_PT', // Portuguese
  vi_VN: 'vi_VN', // Vietnamese
  fr_FR: 'fr_FR', // French
  ru_RU: 'ru_RU', // Russian
  it_IT: 'it_IT', // Italian
}

// 定义 AntdConfigProvider 组件的 props 类型
type TAntdConfigProvideProps = {
  children: React.ReactNode // 接收 React 节点作为子组件
  lang: TLanguages // 语言设置
}

/**
 * @description AntdConfigProvider 組件，用於配置 Ant Design 組件的全域主題。
 *  它通過 ConfigProvider 組件，將主題配置應用到其所有子組件。
 * @param {TAntdConfigProvideProps} props - 組件的 props
 * @returns {JSX.Element} - ConfigProvider 組件
 */
export const AntdConfigProvider = ({ children, lang }: TAntdConfigProvideProps) => {
  // 從 appStore 中獲取主題設定
  const theme = useAppSetting('theme')
  const forceTheme = useAppSetting('forceTheme')
  // 用於儲存動態載入的語言包
  const [locale, setLocale] = useState<Locale | undefined>(undefined)

  useEffect(() => {
    const langCode = langMap[lang] || lang
    const loadLocale = async () => {
      try {
        const localeModule = await import(`antd/es/locale/${langCode}.js`)
        setLocale(localeModule.default)
      } catch (error) {
        console.error(error)
        try {
          const defaultLocale = await import('antd/es/locale/en_US.js')
          setLocale(defaultLocale.default)
        } catch (fallbackError) {
          console.error(fallbackError)
        }
      }
    }

    loadLocale()
  }, [lang])

  // 根據主題設定，選擇不同的 Ant Design 演算法
  const antdThemeConfig = {
    algorithm:
      (forceTheme !== undefined ? forceTheme : theme) === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    components: {
      Slider: {
        dotSize: 12,
      },
      Select: {
        colorBorder: theme === 'dark' ? '#999999' : '',
        colorPrimaryHover: theme === 'dark' ? '#999999' : '',
        activeBorderColor: theme === 'dark' ? '#999999' : '',
      },
    },
  }

  if (!locale) return <ConfigProvider theme={antdThemeConfig}>{children}</ConfigProvider>

  return (
    <ConfigProvider locale={locale} theme={antdThemeConfig}>
      {children}
    </ConfigProvider>
  )
}
